/**
 * TUVI - Actualizaciones del proyecto.
 * Ciclo: registrar el cambio (texto + adjunto opcional) → descargar el formato y
 * enviarlo al cliente para que lo firme → subir el firmado como comprobante.
 * Archivos en el bucket MinIO "actualizaciones".
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { UPLOAD_URL } from '../../../config';
import client from '../../../services/graphqlClient';
import ActualizacionPDF from './ActualizacionPDF';
import '../../Documentos/Cotizaciones.css';
import './ActualizacionesProyecto.css';

const CAMPOS = 'id consecutivo fecha titulo descripcion adjunto_key adjunto_nombre estado comprobante_key comprobante_nombre fecha_aceptacion usuario_nombre aceptado_por_nombre created_at';
const hoyCO = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
const fechaCorta = (d) => (d ? new Date(d.length === 10 ? d + 'T12:00:00' : d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '');
const urlArchivo = (key) => `${UPLOAD_URL}/actualizacion-archivo/${key.split('/').map(encodeURIComponent).join('/')}?token=${encodeURIComponent(localStorage.getItem('tuvi_token') || '')}`;

const esImagen = (nombre) => /\.(jpe?g|png|webp|gif|bmp)$/i.test(nombre || '');

// Descarga el adjunto con la sesión y lo devuelve como data URL, para incrustarlo
// en el PDF sin depender de permisos entre dominios al generar la imagen.
async function adjuntoComoDataUrl(key) {
  const token = localStorage.getItem('tuvi_token');
  const r = await fetch(`${UPLOAD_URL}/actualizacion-archivo/${key.split('/').map(encodeURIComponent).join('/')}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) throw new Error('No se pudo cargar el adjunto');
  const blob = await r.blob();
  return new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(blob); });
}

async function subirArchivo(file, proyectoId, carpeta) {
  const fd = new FormData();
  fd.append('files', file);
  const token = localStorage.getItem('tuvi_token');
  const r = await fetch(`${UPLOAD_URL}/catalogo/upload?prefix=${encodeURIComponent(`proyecto_${proyectoId}/${carpeta}/`)}&bucket=actualizaciones&unico=1`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
  });
  const d = await r.json();
  const key = d.files?.[0]?.key;
  if (!key) throw new Error(d.message || 'No se pudo subir el archivo');
  return { key, nombre: file.name };
}

const VACIO = { titulo: '', descripcion: '', fecha: hoyCO(), archivo: null, adjunto_key: null, adjunto_nombre: null };

export default function ActualizacionesProyecto({ proyectoId, proyecto, onCambio }) {
  const { success, error } = useNotification();
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [form, setForm] = useState(null); // null = cerrado; { ...VACIO, id? }
  const [guardando, setGuardando] = useState(false);
  const [modalComp, setModalComp] = useState(null); // { act, archivo }
  const [pdfAct, setPdfAct] = useState(null);
  const [generando, setGenerando] = useState(null);
  const pdfRef = useRef(null);
  const info = proyecto?.proyecto || {};

  const cargar = useCallback(async () => {
    try {
      const d = await client.query(`query($p: Int!) { actualizacionesProyecto(proyecto_id: $p) { ${CAMPOS} } }`, { p: proyectoId });
      setLista(d.actualizacionesProyecto || []);
    } catch (e) { error('Error cargando las actualizaciones'); }
    finally { setCargando(false); }
  }, [proyectoId, error]);

  useEffect(() => { cargar(); }, [cargar]);

  const refrescar = async () => { await cargar(); if (onCambio) onCambio(); };

  const guardar = async () => {
    if (!form.titulo.trim()) { error('Escribe un título'); return; }
    if (form.descripcion.trim().length < 5) { error('Describe el cambio'); return; }
    setGuardando(true);
    try {
      let adj = { key: form.adjunto_key, nombre: form.adjunto_nombre };
      if (form.archivo) adj = await subirArchivo(form.archivo, proyectoId, 'adjuntos');
      const input = { titulo: form.titulo.trim(), descripcion: form.descripcion.trim(), fecha: form.fecha, adjunto_key: adj.key || null, adjunto_nombre: adj.nombre || null };
      const r = form.id
        ? (await client.mutate('mutation($id: Int!, $i: ActualizacionInput!) { editarActualizacion(id: $id, input: $i) { success message } }', { id: form.id, i: input })).editarActualizacion
        : (await client.mutate('mutation($p: Int!, $i: ActualizacionInput!) { crearActualizacion(proyecto_id: $p, input: $i) { success message } }', { p: proyectoId, i: input })).crearActualizacion;
      if (!r.success) { error(r.message); return; }
      success(r.message);
      setForm(null);
      await refrescar();
    } catch (e) { error(e.message || 'No se pudo guardar'); }
    finally { setGuardando(false); }
  };

  const subirComprobante = async () => {
    if (!modalComp?.archivo) { error('Adjunta el documento firmado por el cliente'); return; }
    setGuardando(true);
    try {
      const c = await subirArchivo(modalComp.archivo, proyectoId, 'comprobantes');
      const r = (await client.mutate('mutation($id: Int!, $k: String!, $n: String) { subirComprobanteActualizacion(id: $id, comprobante_key: $k, comprobante_nombre: $n) { success message } }', { id: modalComp.act.id, k: c.key, n: c.nombre })).subirComprobanteActualizacion;
      if (!r.success) { error(r.message); return; }
      success(r.message);
      setModalComp(null);
      await refrescar();
    } catch (e) { error(e.message || 'No se pudo subir el comprobante'); }
    finally { setGuardando(false); }
  };

  const quitarComprobante = async (a) => {
    if (!window.confirm(`¿Quitar el comprobante de la actualización #${a.consecutivo}? Volverá a quedar pendiente de firma.`)) return;
    const r = (await client.mutate('mutation($id: Int!) { quitarComprobanteActualizacion(id: $id) { success message } }', { id: a.id })).quitarComprobanteActualizacion;
    if (r.success) { success(r.message); await refrescar(); } else error(r.message);
  };

  const eliminar = async (a) => {
    if (!window.confirm(`¿Eliminar la actualización #${a.consecutivo} "${a.titulo}"?`)) return;
    const r = (await client.mutate('mutation($id: Int!) { eliminarActualizacion(id: $id) { success message } }', { id: a.id })).eliminarActualizacion;
    if (r.success) { success(r.message); await refrescar(); } else error(r.message);
  };

  const descargarPDF = async (a) => {
    setGenerando(a.id);
    let imagen = null;
    if (a.adjunto_key && esImagen(a.adjunto_nombre)) {
      try { imagen = await adjuntoComoDataUrl(a.adjunto_key); }
      catch (e) { error('No se pudo cargar la imagen adjunta; el formato sale sin ella'); }
    }
    setPdfAct({ ...a, imagen });
    setTimeout(async () => {
      try {
        const el = pdfRef.current?.querySelector('.adicional-page');
        if (!el) throw new Error('sin contenido');
        const { default: jsPDF } = await import('jspdf');
        const { default: html2canvas } = await import('html2canvas');
        const canvas = await html2canvas(el, { scale: 2, useCORS: true, allowTaint: true, backgroundColor: '#ffffff', imageTimeout: 15000 });
        const pdfW = 612;
        const pdfH = (canvas.height * pdfW) / canvas.width;
        const pdf = new jsPDF({ unit: 'pt', format: [pdfW, pdfH], orientation: 'portrait' });
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.98), 'JPEG', 0, 0, pdfW, pdfH);
        const limpio = (t) => String(t || '').trim().replace(/\s+/g, '-');
        pdf.save(`ACTUALIZACION-${a.consecutivo}-${limpio(info.nombre_cliente)}-${limpio(info.proyecto_nombre)}.pdf`);
        success('Formato descargado');
      } catch (e) {
        console.error(e);
        error('Error generando el PDF');
      } finally {
        setGenerando(null);
        setPdfAct(null);
      }
    }, 1200);
  };

  const pendientes = lista.filter(a => a.estado === 'pendiente').length;

  return (
    <div className="act-proyecto">
      <div className="act-header">
        <div>
          <h2>Actualizaciones</h2>
          <p>Cambios acordados con el cliente. Descarga el formato, envíalo para firma y sube el firmado como comprobante.</p>
        </div>
        {!form && <button className="act-btn-nueva" onClick={() => setForm({ ...VACIO })}>+ Nueva actualización</button>}
      </div>

      {pendientes > 0 && !form && (
        <div className="act-aviso">{pendientes} actualización{pendientes === 1 ? '' : 'es'} pendiente{pendientes === 1 ? '' : 's'} de firma del cliente.</div>
      )}

      {form && (
        <div className="act-form">
          <h3>{form.id ? `Editar actualización #${form.consecutivo}` : 'Nueva actualización'}</h3>
          <div className="act-form-fila">
            <label className="act-campo act-campo-titulo">
              <span>Título</span>
              <input value={form.titulo} maxLength={200} placeholder="Ej: Cambio de enchape del baño social" onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} />
            </label>
            <label className="act-campo">
              <span>Fecha</span>
              <input type="date" value={form.fecha || ''} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} />
            </label>
          </div>
          <label className="act-campo">
            <span>Descripción del cambio</span>
            <textarea rows={6} value={form.descripcion} placeholder="Describe el cambio acordado con el cliente: qué se modifica, dónde y en qué condiciones." onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} />
          </label>
          <label className="act-campo">
            <span>Adjunto (opcional)</span>
            <input type="file" onChange={e => setForm(f => ({ ...f, archivo: e.target.files?.[0] || null }))} />
            {form.adjunto_nombre && !form.archivo && (
              <small>Actual: {form.adjunto_nombre} <button type="button" className="act-link" onClick={() => setForm(f => ({ ...f, adjunto_key: null, adjunto_nombre: null }))}>quitar</button></small>
            )}
          </label>
          <div className="act-form-acciones">
            <button className="act-btn-sec" onClick={() => setForm(null)} disabled={guardando}>Cancelar</button>
            <button className="act-btn-prim" onClick={guardar} disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</button>
          </div>
        </div>
      )}

      {cargando ? <div className="act-vacio">Cargando…</div> : lista.length === 0 ? (
        <div className="act-vacio">No hay actualizaciones registradas en este proyecto.</div>
      ) : (
        <div className="act-lista">
          {lista.map(a => (
            <div key={a.id} className={`act-card ${a.estado}`}>
              <div className="act-card-top">
                <div className="act-card-titulo">
                  <span className="act-num">#{a.consecutivo}</span>
                  <strong>{a.titulo}</strong>
                </div>
                <span className={`act-estado ${a.estado}`}>{a.estado === 'aceptada' ? 'Aceptada' : 'Pendiente de firma'}</span>
              </div>
              <p className="act-desc">{a.descripcion}</p>
              <div className="act-meta">
                <span>{fechaCorta(a.fecha)}{a.usuario_nombre ? ` · registró ${a.usuario_nombre}` : ''}</span>
                {a.estado === 'aceptada' && <span>Aceptada el {fechaCorta(a.fecha_aceptacion)}{a.aceptado_por_nombre ? ` · subió ${a.aceptado_por_nombre}` : ''}</span>}
              </div>
              <div className="act-acciones">
                <button className="act-btn-sec" onClick={() => descargarPDF(a)} disabled={generando === a.id}>{generando === a.id ? 'Generando…' : 'Descargar formato'}</button>
                {a.adjunto_key && <a className="act-btn-sec" href={urlArchivo(a.adjunto_key)} target="_blank" rel="noreferrer">Ver adjunto</a>}
                {a.comprobante_key && <a className="act-btn-ok" href={urlArchivo(a.comprobante_key)} target="_blank" rel="noreferrer">Ver comprobante firmado</a>}
                {a.estado === 'pendiente' && <button className="act-btn-prim" onClick={() => setModalComp({ act: a, archivo: null })}>Subir comprobante firmado</button>}
                {a.estado === 'pendiente' && <button className="act-btn-sec" onClick={() => setForm({ ...VACIO, ...a, archivo: null })}>Editar</button>}
                {a.estado === 'pendiente' && <button className="act-btn-del" onClick={() => eliminar(a)}>Eliminar</button>}
                {a.estado === 'aceptada' && <button className="act-link" onClick={() => quitarComprobante(a)}>Quitar comprobante</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalComp && (
        <div className="act-modal-overlay" onClick={() => !guardando && setModalComp(null)}>
          <div className="act-modal" onClick={e => e.stopPropagation()}>
            <h3>Comprobante de aceptación · #{modalComp.act.consecutivo}</h3>
            <p>Sube el formato firmado por el cliente (PDF o foto). La actualización quedará aceptada.</p>
            <input type="file" accept=".pdf,image/*" onChange={e => setModalComp(m => ({ ...m, archivo: e.target.files?.[0] || null }))} />
            <div className="act-form-acciones">
              <button className="act-btn-sec" onClick={() => setModalComp(null)} disabled={guardando}>Cancelar</button>
              <button className="act-btn-prim" onClick={subirComprobante} disabled={guardando || !modalComp.archivo}>{guardando ? 'Subiendo…' : 'Subir y aceptar'}</button>
            </div>
          </div>
        </div>
      )}

      {pdfAct && (
        <div ref={pdfRef} style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <ActualizacionPDF actualizacion={pdfAct} proyecto={info} />
        </div>
      )}
    </div>
  );
}
